import { Dispatch } from '@reduxjs/toolkit';
import { randomUUID as uuidv4 } from 'expo-crypto';
import { ICartEntry } from 'features/cart/types';
import {
  buildMinimalCartEntriesLookup,
  findMatchingCartEntry,
  mapHydratedCart,
  mapServiceModeToCartServiceMode,
} from 'features/cart/utils/minimal-cart';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import {
  IHydrateCartInput,
  IMenuSelectionInput,
  Platform,
  ServiceMode,
  useHydrateCartMutation,
} from 'generated/graphql-gateway';
import { IComboOfferIncentiveFragment } from 'generated/sanity-graphql';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions } from 'state/global-state';
import { IOffersState } from 'state/global-state/models/loyalty/offers/offers.types';
import { LoyaltyOffer } from 'state/loyalty/types';
import { createCartEntriesFromQuantity } from 'utils/cart';
import { isWeb } from 'utils/environment';
import logger from 'utils/logger';

import { FetchComboFn } from './use-cart-hydration.ts/types';
import {
  fillMissingSanityData,
  generateCartEntryUrl,
  isOfferCombo,
} from './use-cart-hydration.ts/utils';
import { IUseCart } from './use-cart-types';

const getCartId = (cartEntry: ILegacyCartEntry): string => {
  // The app heavily relies on the DISCOUNT_OFFER_CART_ID to identify the discount offer. We should not change it.
  const isDiscountOffer = cartEntry.cartId === DISCOUNT_OFFER_CART_ID;
  const isOfferComboEntry = isOfferCombo(cartEntry);

  const cartId = isDiscountOffer
    ? DISCOUNT_OFFER_CART_ID
    : isOfferComboEntry
    ? cartEntry.cartId
    : uuidv4();

  return cartId;
};

const addCartEntriesByQuantity = (
  cartEntry: ILegacyCartEntry,
  quantity: number,
  addItemToCart: IUseCart['addItemToCart']
) => {
  Array.from({ length: quantity }).forEach(() => {
    const cartId = getCartId(cartEntry);
    addItemToCart(
      {
        ...cartEntry,
        quantity: 1,
        cartId,
        lineId: cartId,
      },
      undefined,
      false,
      true
    );
  });
};

// Had to extract this to a pure function to avoid a circular dep between use-cart and use-minimal-cart
export async function hydrateEntries(
  cartEntries: ICartEntry[],
  storeId: string,
  serviceMode: ServiceMode,
  hydrateCart: ReturnType<typeof useHydrateCartMutation>[0],
  addItemToCart: IUseCart['addItemToCart'],
  updateCartEntry: IUseCart['updateCartEntry'],
  dispatch: Dispatch,
  handleRewards?: (
    rewardBenefitId: string,
    cartEntry: ILegacyCartEntry,
    remainingPoints: { value: number }
  ) => ILegacyCartEntry,
  stagedCartPoints: number = 0
): Promise<boolean> {
  const channel = isWeb ? Platform.WEB : Platform.APP;
  const menuSelections: IMenuSelectionInput[] = cartEntries.map(entry => ({
    id: entry.id,
    quantity: entry.quantity,
    menuSelections: entry.menuSelections,
  }));
  const input: IHydrateCartInput = {
    cart: { menuSelections },
    serviceMode: mapServiceModeToCartServiceMode(serviceMode),
    storeId,
  };

  try {
    const hydrationResult = await hydrateCart({
      variables: { channel, input },
    });

    if (hydrationResult.errors) {
      throw hydrationResult.errors;
    }

    const hydratedCartEntries = mapHydratedCart(
      hydrationResult?.data?.hydrateCart.cartEntries ?? [],
      buildMinimalCartEntriesLookup(cartEntries)
    );

    // Initialize remainingPoints outside the loop
    const remainingPoints = { value: stagedCartPoints };

    hydratedCartEntries.forEach(cartEntry => {
      const matchingMinimalCartEntry = findMatchingCartEntry(cartEntries, cartEntry);
      if (matchingMinimalCartEntry?.isEdit) {
        // If there is only one cart entry to edit
        if (matchingMinimalCartEntry.quantity === 1) {
          updateCartEntry(cartEntry, cartEntry.cartId, true, true);
        } else {
          // if there is more than one cart entry to edit, first we edit the original cart entry
          updateCartEntry({ ...cartEntry, quantity: 1 }, cartEntry.cartId, true, true);

          // Then we add the copies for the rest of entries by quantity with different ids
          addCartEntriesByQuantity(cartEntry, cartEntry.quantity - 1, addItemToCart);
        }
      } else if (handleRewards && matchingMinimalCartEntry?.rewardBenefitId) {
        const rewardBenefitId = matchingMinimalCartEntry.rewardBenefitId;
        // For rewards, we need to create a set of cartEntries based on the quantity.
        // This will ensure we can run one cartEntry at a time to avoid having race condition issues.
        const cartEntriesFromQuantity = createCartEntriesFromQuantity(cartEntry);

        cartEntriesFromQuantity.forEach(cartEntry => {
          // This will create the applied reward for the cartEntry.
          handleRewards(rewardBenefitId, cartEntry, remainingPoints);
          addItemToCart(cartEntry, undefined, false, true);
        });
      } else {
        addCartEntriesByQuantity(cartEntry, cartEntry.quantity, addItemToCart);
      }
    });
    dispatch(actions.ordering.resetMinimalCart());
    return true;
  } catch (error) {
    dispatch(actions.ordering.resetMinimalCart());
    return false;
  }
}

const offerHasBenefit = (offer: any): offer is LoyaltyOffer =>
  !!offer && Array.isArray((offer as any).incentives);

/**
 * Updates the cart entries by syncing them with the offers data.
 * This is required in order to generate the proper cart entry URL.
 *
 * @param {ILegacyCartEntry[]} cartEntries - The hydrated cart entries to update.
 * @param {CmsOffer[]} cmsOffers - The CMS offers used to sync cart entry data.
 *
 * @returns {ILegacyCartEntry[]} The updated cart entries with corrected identifiers and URLs.
 */
const updateHydratedCartEntries = (
  cartEntries: ILegacyCartEntry[],
  cmsOffers: IOffersState['cmsOffers']
): ILegacyCartEntry[] => {
  const offersById = new Map(cmsOffers.map(offer => [offer._id, offer]));

  return cartEntries.map(entry => {
    if (!isOfferCombo(entry)) {
      return {
        ...entry,
        url: generateCartEntryUrl(entry),
      };
    }

    const offer = offersById.get(entry.sanityId);
    const benefitId = offerHasBenefit(offer)
      ? (offer.incentives?.[0] as IComboOfferIncentiveFragment)?._id
      : undefined;

    const _id = benefitId ?? entry._id;

    return {
      ...entry,
      _id,
      sanityId: _id,
      url: generateCartEntryUrl(entry, benefitId),
    };
  });
};

// @TODO: Investigate in order to remove hydrateAndPersistEntries.
// Because now we hydrate immediately in the 1M PDP, this could mean that
// there is no possible state in the app where we could have pending minimal cart entries to hydrate.
// This method was originaly created to hydrate pending minimal cart entries at store change.
/**
 * Copy of `hydrateEntries` with additional tweaks to handle cart hydration at store change.
 * 1. Ensure the hydrated cart entries have the proper URL and identifiers.
 * 2. Fill the cart entries with the necessary Sanity information so they can be repriced properly.
 *
 * @param {ICartEntry[]} cartEntries - Minimal cart entries to hydrate.
 * @param {string} storeId - The ID of the store for which hydration is performed.
 * @param {FetchComboFn} fetchCombo - Function to fetch combo data from Sanity.
 * @param {ServiceMode} serviceMode - The current service mode.
 * @param {ReturnType<typeof useHydrateCartMutation>[0]} hydrateCart - GraphQL mutation to hydrate cart entries.
 * @param {IUseCart['addItemToCart']} addItemToCart - Function to add items to the cart state.
 * @param {IUseCart['updateCartEntry']} updateCartEntry - Function to update an existing cart entry.
 * @param {Dispatch} dispatch - Redux dispatch function to update global state.
 * @param {(LoyaltyOffer | ISystemwideTierGroupFragment)[]} cmsOffers - CMS offers used to sync entries for correct URLs and IDs.
 * @param {(rewardBenefitId: string, cartEntry: ILegacyCartEntry, remainingPoints: { value: number }) => ILegacyCartEntry} [handleRewards] - Optional function to handle reward-based cart entries.
 * @param {number} [stagedCartPoints=0] - Initial staged loyalty points to allocate for rewards.
 *
 * @returns {Promise<boolean>} Resolves to `true` if hydration and persistence succeed, otherwise `false`.
 */
export async function hydrateAndPersistEntries(
  cartEntries: ICartEntry[],
  storeId: string,
  fetchCombo: FetchComboFn,
  serviceMode: ServiceMode,
  hydrateCart: ReturnType<typeof useHydrateCartMutation>[0],
  addItemToCart: IUseCart['addItemToCart'],
  updateCartEntry: IUseCart['updateCartEntry'],
  dispatch: Dispatch,
  cmsOffers: IOffersState['cmsOffers'],
  handleRewards?: (
    rewardBenefitId: string,
    cartEntry: ILegacyCartEntry,
    remainingPoints: { value: number }
  ) => ILegacyCartEntry,
  stagedCartPoints: number = 0
): Promise<boolean> {
  const channel = isWeb ? Platform.WEB : Platform.APP;
  const menuSelections: IMenuSelectionInput[] = cartEntries.map(entry => ({
    id: entry.id,
    quantity: entry.quantity,
    menuSelections: entry.menuSelections,
  }));
  const input: IHydrateCartInput = {
    cart: { menuSelections },
    serviceMode: mapServiceModeToCartServiceMode(serviceMode),
    storeId,
  };

  try {
    const hydrationResult = await hydrateCart({
      variables: { channel, input },
    });

    if (hydrationResult.errors) {
      throw hydrationResult.errors;
    }

    const hydratedCartEntries = mapHydratedCart(
      hydrationResult?.data?.hydrateCart.cartEntries ?? [],
      buildMinimalCartEntriesLookup(cartEntries)
    );

    // Initialize remainingPoints outside the loop
    const remainingPoints = { value: stagedCartPoints };

    // Ensure the cart entries to have the proper types and ids for ALC and Offer entries.
    const updatedHydratedEntries = updateHydratedCartEntries(hydratedCartEntries, cmsOffers);

    // Ensure the entries to have all the necessary sanity information (ie: mainItem, enableUpsellPricing, etc.)
    const entries = await fillMissingSanityData({
      cartEntries: updatedHydratedEntries,
      fetchCombo,
    });

    entries.forEach(cartEntry => {
      const matchingMinimalCartEntry = findMatchingCartEntry(cartEntries, cartEntry);
      if (matchingMinimalCartEntry?.isEdit) {
        // If there is only one cart entry to edit
        if (matchingMinimalCartEntry.quantity === 1) {
          updateCartEntry(cartEntry, cartEntry.cartId, true, true);
        } else {
          // if there is more than one cart entry to edit, first we edit the original cart entry
          updateCartEntry({ ...cartEntry, quantity: 1 }, cartEntry.cartId, true, true);

          // Then we add the copies for the rest of entries by quantity with different ids
          addCartEntriesByQuantity(cartEntry, cartEntry.quantity - 1, addItemToCart);
        }
      } else if (handleRewards && matchingMinimalCartEntry?.rewardBenefitId) {
        const rewardBenefitId = matchingMinimalCartEntry.rewardBenefitId;
        // For rewards, we need to create a set of cartEntries based on the quantity.
        // This will ensure we can run one cartEntry at a time to avoid having race condition issues.
        const cartEntriesFromQuantity = createCartEntriesFromQuantity(cartEntry);

        cartEntriesFromQuantity.forEach(cartEntry => {
          // This will create the applied reward for the cartEntry.
          handleRewards(rewardBenefitId, cartEntry, remainingPoints);
          addItemToCart(cartEntry, undefined, false, true);
        });
      } else {
        addCartEntriesByQuantity(cartEntry, cartEntry.quantity, addItemToCart);
      }
    });
    dispatch(actions.ordering.resetMinimalCart());
    return true;
  } catch (error) {
    dispatch(actions.ordering.resetMinimalCart());
    return false;
  }
}

interface IHydrateAndReturnEntries {
  cartEntries: ICartEntry[];
  storeId: string;
  serviceMode: ServiceMode;
  hydrateCart: ReturnType<typeof useHydrateCartMutation>[0];
}

/**
 * Hydrates minimal cart entries by calling the GraphQL `hydrateCart` mutation
 * and returns the resulting fully hydrated legacy cart entries.
 *
 * @param {IHydrateAndReturnEntries} params - Parameters for hydration.
 * @param {IMinimalCartEntry[]} params.cartEntries - Minimal cart entries to be hydrated.
 * @param {string} params.storeId - The ID of the store to hydrate the cart for.
 * @param {'pickup' | 'delivery'} params.serviceMode - The selected service mode for the cart.
 * @param {(options: { variables: { channel: Platform; input: IHydrateCartInput } }) => Promise<HydrationResponse>} params.hydrateCart - The GraphQL mutation function used to perform hydration.
 *
 * @returns {Promise<ILegacyCartEntry[] | null>} A promise that resolves to hydrated legacy cart entries,
 * or `null` if the hydration process fails.
 */
export const hydrateAndReturnEntries = async ({
  cartEntries,
  storeId,
  serviceMode,
  hydrateCart,
}: IHydrateAndReturnEntries): Promise<ILegacyCartEntry[] | null> => {
  const channel = isWeb ? Platform.WEB : Platform.APP;

  const menuSelections: IMenuSelectionInput[] = cartEntries.map(entry => ({
    id: entry.id,
    quantity: entry.quantity,
    menuSelections: entry.menuSelections,
  }));

  const input: IHydrateCartInput = {
    cart: { menuSelections },
    serviceMode: mapServiceModeToCartServiceMode(serviceMode),
    storeId,
  };

  try {
    const hydrationResult = await hydrateCart({
      variables: { channel, input },
    });

    if (hydrationResult.errors) {
      throw hydrationResult.errors;
    }

    const hydratedCartEntries = mapHydratedCart(
      hydrationResult?.data?.hydrateCart.cartEntries ?? [],
      buildMinimalCartEntriesLookup(cartEntries)
    );

    return hydratedCartEntries;
  } catch (error) {
    logger.error({
      message: 'Minimal Cart Entries Hydration Failed',
      newStoreId: storeId,
      cartEntries: JSON.stringify(cartEntries),
      error: JSON.stringify(error),
    });

    return null;
  }
};
